// chatNotifications.js - IMPROVED VERSION
import { subscribeToAllChats, getUserRoleForChat } from '@services/firebaseChat';

class ChatNotificationService {
    constructor() {
        this.unsubscribeCallbacks = new Map();
        this.notificationCallbacks = new Map();
        this.unreadCounts = new Map();
        this.notificationPermission = 'default';
    }

    // Request browser notification permission
    async requestPermission() {
        if (!('Notification' in window)) return 'denied';
        if (Notification.permission === 'granted') {
            this.notificationPermission = 'granted';
            return 'granted';
        }
        if (Notification.permission !== 'denied') {
            const result = await Notification.requestPermission();
            this.notificationPermission = result;
            return result;
        }
        this.notificationPermission = 'denied';
        return 'denied';
    }

    // Update app icon badge with unread count
    async updateBadge(count) {
        try {
            if ('setAppBadge' in navigator) {
                if (count > 0) {
                    await navigator.setAppBadge(count);
                } else {
                    await navigator.clearAppBadge();
                }
            }
        } catch (err) {
            // Silently fail — Badging API not supported or not allowed
        }
    }

    // Show browser-level notification
    async showBrowserNotification(title, body) {
        // Check permission directly from the API, not the cached value
        if (!('Notification' in window) || Notification.permission !== 'granted') return;

        try {
            // Try service worker first (works in background)
            if ('serviceWorker' in navigator) {
                const registration = await navigator.serviceWorker.ready;
                if (registration.active) {
                    registration.active.postMessage({
                        type: 'SHOW_NOTIFICATION',
                        title,
                        body,
                        icon: '/pcakelogo-192.png',
                        tag: 'chat-notification',
                        data: { url: '/admin-chat' },
                    });
                    return;
                }
            }
            // Fallback to basic Notification API
            new Notification(title, {
                body,
                icon: '/pcakelogo-192.png',
                tag: 'chat-notification',
            });
        } catch (err) {
            // Silently fail
        }
    }

    // In chatNotifications.js - FIX FOR SUPER/ADMIN
    subscribeToNotifications(userId, callback) {
        if (!userId) return;

        this.unsubscribeCallbacks.get(userId)?.();

        const unsubscribe = subscribeToAllChats(userId, async (chats) => {
            try {
                const userRole = await getUserRoleForChat(userId);                
                let unreadCount = 0;
                chats.forEach((chat) => {
                    if (chat.bookmarked === true && chat.messages) {                        
                        const chatUnread = chat.messages.filter(message => {
                            const isUnread = !message.read;
                            
                            // COMMON LOGIC FOR BOTH ADMIN AND SUPER
                            const isForAdminOrSuper = 
                                // Case 1: Regular messages addressed to this user
                                message.receiverId === userId ||
                                // Case 2: Support messages from users (receiverId is null)
                                (message.receiverId === null && message.senderRole !== userRole) ||
                                // Case 3: Admin reading Super's messages (stored in Super profile)
                                (userRole === 'Admin' && message.senderId !== userId && chat.userRole === 'Super') ||
                                // Case 4: Super reading Admin's messages (stored in Super profile)  
                                (userRole === 'Super' && message.senderRole === 'Admin' && message.receiverId === userId);

                            if (isUnread && isForAdminOrSuper) {
                                return true;
                            }
                            return false;
                        }).length;

                        unreadCount += chatUnread;
                    }
                });
                const previousCount = this.unreadCounts.get(userId);
                this.unreadCounts.set(userId, unreadCount);

                // Update app icon badge
                this.updateBadge(unreadCount);

                // Browser notifications are now handled by FCM push (Cloud Function)
                // No need to call showBrowserNotification here

                callback(unreadCount);

            } catch (error) {
                console.error('❌ Error:', error);
                this.unreadCounts.set(userId, 0);
                callback(0);
            }
        });

        this.unsubscribeCallbacks.set(userId, unsubscribe);
        this.notificationCallbacks.set(userId, callback);
        return unsubscribe;
    }
    // Get current unread count for a user
    getUnreadCount(userId) {
        if (!userId) return 0;
        return this.unreadCounts.get(userId) || 0;
    }

    // Unsubscribe from notifications
    unsubscribe(userId) {
        if (!userId) return;

        try {
            this.unsubscribeCallbacks.get(userId)?.();
            this.unsubscribeCallbacks.delete(userId);
            this.notificationCallbacks.delete(userId);
            this.unreadCounts.delete(userId);
        } catch (error) {
            console.error('❌ Error unsubscribing from notifications:', error);
        }
    }

    // Force refresh notifications (useful for testing)
    async forceRefresh(userId) {
        if (!userId) return;

        const callback = this.notificationCallbacks.get(userId);
        if (callback) {
            // Trigger the real-time listener manually
            const currentCount = this.unreadCounts.get(userId) || 0;
            callback(currentCount);
        }
    }
}

export const chatNotificationService = new ChatNotificationService();